(() => {
  const selector = '[data-runcam-reveal]';
  const existingController = window.runcamDetailRevealController;

  if (existingController) {
    existingController.scan(document, true);
    return;
  }

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const timers = new Map();
  const animationHandlers = new Map();
  const observedElements = new Set();

  const clearAnimation = (element) => {
    const timer = timers.get(element);
    const handler = animationHandlers.get(element);

    if (timer) window.clearTimeout(timer);
    if (handler) element.removeEventListener('animationend', handler);
    timers.delete(element);
    animationHandlers.delete(element);
  };

  const complete = (element) => {
    clearAnimation(element);
    observer?.unobserve(element);
    observedElements.delete(element);
    element.classList.remove('is-reveal-pending', 'is-revealing', 'runcam-detail-reveal');
    element.style.removeProperty('--runcam-reveal-delay');
    element.dataset.runcamRevealComplete = 'true';
  };

  const reveal = (element) => {
    observer?.unobserve(element);
    observedElements.delete(element);

    if (reduceMotion.matches) {
      complete(element);
      return;
    }

    const order = Number.parseInt(element.dataset.runcamRevealOrder || '0', 10);
    const delay = Math.min(Math.max(Number.isNaN(order) ? 0 : order, 0) * 70, 350);
    const handleAnimationEnd = (event) => {
      if (event.target === element && event.animationName === 'runcam-detail-reveal-in') complete(element);
    };

    element.style.setProperty('--runcam-reveal-delay', `${delay}ms`);
    element.addEventListener('animationend', handleAnimationEnd);
    animationHandlers.set(element, handleAnimationEnd);
    element.classList.add('is-revealing');
    timers.set(element, window.setTimeout(() => complete(element), 850 + delay));
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const requiredHeight = Math.min(entry.boundingClientRect.height * 0.15, 120);
            if (entry.isIntersecting && entry.intersectionRect.height >= requiredHeight) reveal(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: [0, 0.01, 0.05, 0.1, 0.15] }
      )
    : null;

  const prepare = (element, revealVisibleImmediately) => {
    if (element.dataset.runcamRevealBound === 'true') return;

    element.dataset.runcamRevealBound = 'true';
    element.classList.add('runcam-detail-reveal', 'is-reveal-pending');

    if (!observer || reduceMotion.matches) {
      complete(element);
      return;
    }

    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      complete(element);
      return;
    }

    const isVisible = rect.bottom > 0 && rect.top < window.innerHeight * 0.92;
    if (revealVisibleImmediately && isVisible) {
      complete(element);
      return;
    }

    observedElements.add(element);
    observer.observe(element);
  };

  const scan = (root = document, revealVisibleImmediately = false) => {
    if (!(root instanceof Element || root instanceof Document)) return;

    if (root instanceof Element && root.matches(selector)) prepare(root, revealVisibleImmediately);
    root.querySelectorAll(selector).forEach((element) => prepare(element, revealVisibleImmediately));
  };

  const cleanup = (root) => {
    if (!(root instanceof Element)) return;

    const elements = root.matches(selector) ? [root, ...root.querySelectorAll(selector)] : root.querySelectorAll(selector);
    elements.forEach((element) => {
      observer?.unobserve(element);
      observedElements.delete(element);
      clearAnimation(element);
    });
  };

  const handleMotionPreference = () => {
    if (!reduceMotion.matches) return;
    observedElements.forEach((element) => complete(element));
    timers.forEach((_, element) => complete(element));
  };

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:reorder', () => scan(document));
  document.addEventListener('shopify:section:unload', (event) => cleanup(event.target));
  reduceMotion.addEventListener?.('change', handleMotionPreference);

  window.runcamDetailRevealController = { scan, cleanup };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document, true), { once: true });
  } else {
    scan(document, true);
  }
})();
